import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActiveCartService, MultiCartService, SelectiveCartService } from "@spartacus/cart/base/core";
import { CartItemComponentOptions, ConsignmentEntry, OrderEntry, PromotionLocation } from "@spartacus/cart/base/root";
import { FeatureConfigService, UserIdService } from '@spartacus/core';
import { Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
    selector: 'app-uig-cart-item-list',
    templateUrl: './uig-cart-item-list.component.html',
    styleUrls: ['./uig-cart-item-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class UigCartItemListComponent implements OnInit, OnDestroy {

  private subscription = new Subscription();
  private userId: string;

  @Input() readonly: boolean = false;

  @Input() hasHeader: boolean = true;
  @Input() savedCarts?: boolean;

  @Input() options: CartItemComponentOptions = {
    isSaveForLater: false,
    optionalBtn: null,
  };

  @Input() cartId: string;

  private _items: OrderEntry[] = [];
  form: UntypedFormGroup = this.featureConfigService?.isLevel('3.1')
    ? new UntypedFormGroup({})
    : undefined;

  @Input('items')
  set items(items: OrderEntry[]) {
    this.resolveItems(items);
    this.createForm();
  }
  get items(): OrderEntry[] {
    return this._items;
  }

  @Input() promotionLocation: PromotionLocation = PromotionLocation.ActiveCart;

  @Input('cartIsLoading') set setLoading(value: boolean) {
    if (!this.readonly) {
      // Whenever the cart is loading, we disable the complete form
      // to avoid any user interaction with the cart.
      value
        ? this.form.disable({ emitEvent: false })
        : this.form.enable({ emitEvent: false });
    }
  }

  constructor(
    protected activeCartService: ActiveCartService,
    protected selectiveCartService: SelectiveCartService,
    public featureConfigService?: FeatureConfigService,
    protected userIdService?: UserIdService,
    protected multiCartService?: MultiCartService
  ) { }

  ngOnInit(): void {
    this.subscription.add(
      this.userIdService
        ?.getUserId()
        .subscribe((userId) => (this.userId = userId))
    );
  }

  private createForm(): void {
    if (!this.featureConfigService?.isLevel('3.1')) {
      this.form = new UntypedFormGroup({});
    }

    this._items.forEach((item) => {
      const controlName = this.getControlName(item);
      const group = new UntypedFormGroup({
        entryNumber: new UntypedFormControl(item.entryNumber),
        quantity: new UntypedFormControl(item.quantity, { updateOn: 'blur' }),
      });
      if (!item.updateable || this.readonly) {
        group.disable();
      }
      this.form.addControl(controlName, group);
    });
  }

  protected getControlName(item: OrderEntry): string {
    return item.entryNumber.toString();
  }

  removeEntry(item: OrderEntry): void {
    if (this.selectiveCartService && this.options.isSaveForLater) {
      this.selectiveCartService.removeEntry(item);
    } else if (this.cartId && this.userId) {
      this.multiCartService?.removeEntry(
        this.userId,
        this.cartId,
        item.entryNumber
      );
    } else {
      this.activeCartService.removeEntry(item);
    }
    delete this.form.controls[this.getControlName(item)];
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  /**
   * method changed
   */
  getControl(item: OrderEntry): Observable<UntypedFormGroup> {
    return this.form.get(this.getControlName(item)).valueChanges.pipe(
      startWith(null),
      map(() => <UntypedFormGroup>this.form.get(this.getControlName(item)))
    );
  }

  protected resolveItems(items: OrderEntry[]): void {
    if (!items) {
      this._items = [];
      return;
    }

    // The items we're getting from the input do not have a consistent model.
    // In case of a `consignmentEntry`, we need to normalize the data from the orderEntry.
    if (items.every((item) => item.hasOwnProperty('orderEntry'))) {
      this.normalizeConsignmentEntries(items);
    } else {
      this.rerenderChangedItems(items);
    }
  }

  protected normalizeConsignmentEntries(items: OrderEntry[]) {
    this._items = items.map((consignmentEntry) => {
      const entry = Object.assign(
        {},
        (consignmentEntry as ConsignmentEntry).orderEntry
      );
      entry.quantity = consignmentEntry.quantity;
      return entry;
    });
  }

  /**
   * We'd like to avoid the unnecessary re-renders of unchanged cart items after the data reload.
   * OCC cart entries don't have any unique identifier that we could use in Angular `trackBy`.
   * So we update each array element to the new object only when it's any different to the previous one.
   */
  protected rerenderChangedItems(items: OrderEntry[]) {
    let offset = 0;
    for (
      let i = 0;
      i - offset < Math.max(items.length, this._items.length);
      i++
    ) {
      const index = i - offset;
      if (
        JSON.stringify(this._items?.[index]) !== JSON.stringify(items[index])
      ) {
        if (this._items[index]) {
          this.form?.removeControl(this.getControlName(this._items[index]));
        }
        if (!items[index]) {
          this._items.splice(index, 1);
          offset++;
        } else {
          this._items[index] = items[index];
        }
      }
    }
  }
}
